import { useState, useEffect } from "react";
import { Box, Button, Table, TableCell, TableHead, TableRow } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Fab from "@mui/material/Fab";
import EditIcon from "@mui/icons-material/Edit";
import PopoverComponent from "../Popover/PopoverComponent";
import { useQuery } from "react-query";
import AlertApi from "../../../../services/API/Alert.Api";

const IdealCustomerAlerts = () => {
  const [idealAlertCount, SetIdealAlertCount] = useState(0 as number);
  const [idealRow, SetIdealRow] = useState([] as any);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [name, SetName] = useState("");
  const [note, SetNote] = useState("");
  const open = Boolean(anchorEl);

  // Group comment state:
  const [groupAnchorEl, setGroupAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [groupName, SetGroupName] = useState("");
  const [groupNote, SetGroupNote] = useState("");
  const [isGroupSaving, SetIsGroupSaving] = useState(false);
  const groupOpen = Boolean(groupAnchorEl);

  const classes = useStyles();

  // Get Ideal Customer Alerts:
  const { data: dataIdealAlerts, refetch } = useQuery(
    ["getIdealAlerts"],
    () => AlertApi.getIdealAlerts(),
    {
      initialData: [],
      keepPreviousData: true,
    }
  );

  // Set Alert Count:
  useEffect(() => {
    SetIdealAlertCount(dataIdealAlerts.length);
  }, [dataIdealAlerts]);

  const handleIdealRowClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    currentRow: any
  ) => {
    setAnchorEl(event.currentTarget);
    SetIdealRow(currentRow);
  };

  // Build a tech-representative string from name + note, skipping empty
  // values so we never save a lone ",".
  const buildTechRepresentative = (n: string, nt: string) => {
    const parts = [n, nt]
      .map((p) => (p || "").trim())
      .filter((p) => p.length > 0);
    return parts.join(",");
  };

  // Submit Handler:
  const OnSaveClick = async () => {
    const techRep = buildTechRepresentative(name, note);
    if (techRep === "") {
      // Nothing to save — close the popover and bail out.
      setAnchorEl(null);
      SetName("");
      SetNote("");
      return;
    }

    const req = {
      id: idealRow.id,
      dealerType: idealRow.dealerType,
      serialNumber: idealRow.serialNumber,
      acknowledge: true,
      techRepresentative: techRep,
      name: "string",
      dbserver: "string",
      wvdserver: "string",
      retired: true,
      companyPw: "string",
      dateBuilt: "2022-01-13T10:37:49.636Z",
      dateLive: "2022-01-13T10:37:49.636Z",
      appVersion: "string",
      systemService: "string",
      connector: "string",
      accountNumber: 0,
      numUsers: 0,
      alertCause: "string",
      isEmailSent: true,
    };

    try {
      const res = await AlertApi.acknowledgeIdealAlert(req);
      setAnchorEl(null); // Close Popover onSuccess:
      SetName("");
      SetNote("");
      refetch(); // Refetch Alert data:
    } catch (error) {
      console.log(error);
      setAnchorEl(null); // Close Popover onFailure:
    }
  };

  // Open group comment popover:
  const handleGroupCommentClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setGroupAnchorEl(event.currentTarget);
  };

  // Returns rows that have no existing comment / tech representative.
  const getRowsWithoutComment = () => {
    if (!dataIdealAlerts || dataIdealAlerts.length === 0) return [];
    return dataIdealAlerts.filter(
      (row: any) =>
        row.techRepresentative === null ||
        row.techRepresentative === undefined ||
        String(row.techRepresentative).trim() === ""
    );
  };

  // Group submit handler — applies the comment only to rows without an
  // existing comment. Rows with comments remain unchanged.
  const OnGroupSaveClick = async () => {
    const rowsToUpdate = getRowsWithoutComment();
    if (rowsToUpdate.length === 0) {
      setGroupAnchorEl(null);
      SetGroupName("");
      SetGroupNote("");
      return;
    }

    const techRep = buildTechRepresentative(groupName, groupNote);
    if (techRep === "") {
      // Nothing to save — close the popover and bail out.
      setGroupAnchorEl(null);
      SetGroupName("");
      SetGroupNote("");
      return;
    }

    SetIsGroupSaving(true);
    try {
      await Promise.all(
        rowsToUpdate.map((row: any) => {
          const req = {
            id: row.id,
            dealerType: row.dealerType,
            serialNumber: row.serialNumber,
            acknowledge: true,
            techRepresentative: techRep,
            name: "string",
            dbserver: "string",
            wvdserver: "string",
            retired: true,
            companyPw: "string",
            dateBuilt: "2022-01-13T10:37:49.636Z",
            dateLive: "2022-01-13T10:37:49.636Z",
            appVersion: "string",
            systemService: "string",
            connector: "string",
            accountNumber: 0,
            numUsers: 0,
            alertCause: "string",
            isEmailSent: true,
          };
          return AlertApi.acknowledgeIdealAlert(req);
        })
      );
    } catch (error) {
      console.log(error);
    } finally {
      SetIsGroupSaving(false);
      setGroupAnchorEl(null);
      SetGroupName("");
      SetGroupNote("");
      refetch();
    }
  };

  const rowsWithoutCommentCount = getRowsWithoutComment().length;

  return (
    <Box
      style={{ display: "table", marginTop: "20px", minWidth: "100%" }}
      alignItems="center"
    >
      <fieldset
        style={{
          fontSize: "large",
          fontWeight: "bold",
          border: "1px solid silver",
        }}
      >
        <legend>&nbsp;Ideal Customer Alerts&nbsp;</legend>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          style={{ padding: "4px 4px 8px 4px" }}
        >
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon style={{ fontSize: "16px" }} />}
            disabled={isGroupSaving}
            onClick={handleGroupCommentClick}
            title={
              rowsWithoutCommentCount === 0
                ? "All rows already have comments"
                : `Apply a comment to ${rowsWithoutCommentCount} row(s) without a comment`
            }
          >
            Group Comment
            {rowsWithoutCommentCount > 0
              ? ` (${rowsWithoutCommentCount})`
              : ""}
          </Button>
        </Box>
        <Table
          sx={{ minWidth: 650 }}
          aria-label="simple table"
          className={"myTable"}
        >
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>SerialNumber</TableCell>
              <TableCell>DBServer</TableCell>
              <TableCell>WVDServer</TableCell>
              <TableCell>DateBuilt</TableCell>
              <TableCell>DateLive</TableCell>
              <TableCell>AppVersion</TableCell>
              <TableCell>SystemService</TableCell>
              <TableCell>Connector</TableCell>
              <TableCell>Firebird</TableCell>
              <TableCell>IdealFeed</TableCell>
              <TableCell>Acknowledge</TableCell>
              <TableCell>EmailSent</TableCell>
              <TableCell>TechSupport</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          {idealAlertCount > 0 ? (
            dataIdealAlerts.map((row: any, index: any) => {
              let dateLive = "";
              if (row.dateLive != null)
                dateLive = new Date(row.dateLive).toLocaleDateString();

              let dateBuilt = "";
              if (row.dateBuilt != null)
                dateBuilt = new Date(row.dateBuilt).toLocaleDateString();

              let connectorAlert = "";
              if (row.connector === "Stopped") connectorAlert = classes.alert;

              let serviceAlert = "";
              if (row.systemService === "Stopped") serviceAlert = classes.alert;

              let firebirdAlert = "";
              if (row.firebirdService === "Stopped") firebirdAlert = classes.alert;

              let idealFeedAlert = "";
              if (row.idealFeedService === "Stopped") idealFeedAlert = classes.alert;

              return (
                <TableRow
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  role="checkbox"
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.serialNumber}</TableCell>
                  <TableCell>{row.dbserver}</TableCell>
                  <TableCell>{row.wvdserver}</TableCell>
                  <TableCell>{dateBuilt}</TableCell>
                  <TableCell>{dateLive}</TableCell>
                  <TableCell>{row.appVersion}</TableCell>
                  <TableCell className={serviceAlert}>
                    {row.systemService}
                  </TableCell>
                  <TableCell className={connectorAlert}>
                    {row.connector}
                  </TableCell>
                  <TableCell className={firebirdAlert}>
                    {row.firebirdService}
                  </TableCell>
                  <TableCell className={idealFeedAlert}>
                    {row.idealFeedService}
                  </TableCell>
                  {row.acknowledge === true ? (
                    <TableCell>Yes</TableCell>
                  ) : (
                    <TableCell>No</TableCell>
                  )}
                  {row.isEmailSent === true ? (
                    <TableCell>Yes</TableCell>
                  ) : (
                    <TableCell>No</TableCell>
                  )}
                  <TableCell>{row.techRepresentative}</TableCell>
                  <TableCell>
                    <Fab
                      size="small"
                      aria-label="edit"
                      onClick={(event) => handleIdealRowClick(event, row)}
                    >
                      <EditIcon style={{ fontSize: "18px" }} />
                    </Fab>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow
              hover
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              role="checkbox"
            >
              <TableCell
                align="center"
                colSpan={13}
                style={{
                  fontSize: "18pt",
                  fontWeight: "bold",
                }}
              >
                No Record Found
              </TableCell>
            </TableRow>
          )}
        </Table>
      </fieldset>

      <PopoverComponent
        open={open}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        SetName={SetName}
        SetNote={SetNote}
        OnSaveClick={OnSaveClick}
      />

      <PopoverComponent
        open={groupOpen}
        anchorEl={groupAnchorEl}
        setAnchorEl={setGroupAnchorEl}
        SetName={SetGroupName}
        SetNote={SetGroupNote}
        OnSaveClick={OnGroupSaveClick}
      />
    </Box>
  );
};

export default IdealCustomerAlerts;

const useStyles = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
  },
  alert: { backgroundColor: "#E74C3C", color: "white" },
  alertMedium: { backgroundColor: "orange", color: "white !important" },
  hideCell: {
    display: "none !important",
  },
}));
